import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Award, ShoppingBag, Wine, Utensils, Clock, Sparkles } from "lucide-react";
import api from "../lib/api";
import FleurKintsugi from "../components/brand/FleurKintsugi";
import KintsugiScrollVein from "../components/brand/KintsugiScrollVein";
import Reveal from "../components/story/Reveal";
import ShareButtons from "../components/ShareButtons";
import { isNewProduct } from "../lib/productUtils";

const paras = (t) => (t || "").split(/\n\s*\n/).map((s) => s.trim()).filter(Boolean);

function Section({ eyebrow, title, children, vein, testid }) {
  return (
    <section className="relative overflow-hidden py-14 lg:py-20" data-testid={testid}>
      {vein && <KintsugiScrollVein variant={vein} strokes={2} opacity={0.3} />}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[220px_1fr] gap-6 lg:gap-14">
        <Reveal>
          <div className="smallcaps-label mb-2" style={{ fontSize: "0.65rem" }}>{eyebrow}</div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight">{title}</h2>
        </Reveal>
        <Reveal delay={120} className="story-prose max-w-2xl">{children}</Reveal>
      </div>
    </section>
  );
}

function Aroma({ fiche }) {
  const cols = [["Nez", fiche.nez], ["Bouche", fiche.bouche], ["Finale", fiche.finale]].filter(([, v]) => v);
  return (
    <div className="grid sm:grid-cols-3 gap-4" data-testid="product-aroma">
      {cols.map(([k, v], i) => (
        <Reveal key={k} delay={i * 120} className="kintsugi-card rounded-2xl p-6">
          <div className="font-display text-2xl gold-text mb-2">{k}</div>
          <p className="text-sm text-foreground/85 leading-relaxed" style={{ margin: 0 }}>{v}</p>
        </Reveal>
      ))}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [others, setOthers] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null);
    api.get(`/products/${slug}`).then(({ data }) => setProduct(data)).catch(() => setError(true));
    api.get("/products").then(({ data }) => setOthers(data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="product-not-found">
        <h1 className="font-display text-3xl mb-6">Cette création n'est plus disponible.</h1>
        <Link to="/boutique" className="btn-gold-marble inline-flex items-center gap-2 rounded-full">Retour à la boutique</Link>
      </div>
    );
  }
  if (!product) return <div className="py-40 text-center text-muted-foreground" data-testid="product-loading">Chargement…</div>;

  const f = product.fiche || {};
  const isMedal = product.category === "medaille";
  const isBox = product.category === "coffret";
  const isNew = isNewProduct(product);
  const shortName = product.name.split("—")[0].trim();

  return (
    <div data-testid="product-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="magma-ambient" aria-hidden="true"><span className="magma-blob" /></div>
        <KintsugiScrollVein variant="s-curve" strokes={2} opacity={0.45} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-10 lg:pt-16 pb-14 lg:pb-20">
          <Link to="/boutique" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-primary mb-10" data-testid="product-back-link">
            <ArrowLeft className="w-3.5 h-3.5" /> Boutique
          </Link>
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20 items-center">
            <Reveal className="relative">
              <div className="kintsugi-frame aspect-[4/5] w-full max-w-md mx-auto relative overflow-hidden" data-testid="product-image">
                {isNew && <div className="ribbon-new" data-testid="product-new-ribbon">Nouveauté</div>}
                {product.image
                  ? <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center bg-card"><Wine className="w-20 h-20 text-primary/40" strokeWidth={1} /></div>}
              </div>
              {isMedal && (
                <div className="absolute -top-5 -right-2 lg:-right-6 wax-seal" style={{ width: 110, height: 110, animationDelay: "0.4s" }} data-testid="product-medal-seal">
                  <div className="flex flex-col items-center text-[#3a1f05] px-2">
                    <Award className="w-5 h-5" strokeWidth={2.4} />
                    <span className="font-display text-[11px] font-bold uppercase tracking-[0.12em] leading-tight text-center mt-0.5">{(f.distinction || product.tagline || "").split("—")[0].trim()}</span>
                    <span className="text-[6px] uppercase tracking-[0.2em] opacity-85 mt-1">Frankfurt 2026</span>
                  </div>
                </div>
              )}
            </Reveal>

            <div>
              {isNew && <span className="pill-new reveal-hero mb-4" data-testid="product-new-pill"><Sparkles className="w-3 h-3" /> Nouveauté</span>}
              <div className="smallcaps-label reveal-hero mb-5" style={{ animationDelay: "0.1s" }}>{isMedal ? "Cuvée primée" : isBox ? "Coffret cadeau" : "Création signature"} · {product.format_label || `${product.volume_ml} ml · ${product.abv} % vol.`}</div>
              <h1 className="font-display uppercase leading-[0.95] tracking-tight reveal-hero" style={{ fontSize: "clamp(2.4rem, 5.5vw, 4.6rem)", animationDelay: "0.25s" }} data-testid="product-name">
                <span className="gold-text">{shortName}</span>
              </h1>
              {f.accroche && (
                <p className="font-display text-lg md:text-xl text-foreground/85 mt-7 max-w-xl leading-snug reveal-hero" style={{ animationDelay: "0.45s" }} data-testid="product-accroche">{f.accroche}</p>
              )}
              <div className="kintsugi-divider reveal-hero" style={{ margin: "2rem 0", maxWidth: "9rem", animationDelay: "0.6s" }} />
              <div className="flex flex-wrap items-center gap-6 reveal-hero" style={{ animationDelay: "0.75s" }}>
                <div className="font-display text-4xl gold-text" data-testid="product-price">{product.price.toFixed(2)} €</div>
                <Link to={`/boutique?ajouter=${product.slug}`} className="btn-gold-marble inline-flex items-center gap-3 rounded-full" data-testid="product-add-to-cart">
                  <ShoppingBag className="w-4 h-4" /> {isBox ? "Commander ce coffret" : "Commander cette bouteille"}
                </Link>
              </div>
              {product.ingredients?.length > 0 && (
                <div className="mt-8 text-xs text-muted-foreground reveal-hero" style={{ animationDelay: "0.9s" }} data-testid="product-ingredients">
                  <span className="uppercase tracking-[0.2em] text-foreground/80">Composition : </span>{product.ingredients.join(" · ")}
                </div>
              )}
              <ShareButtons sharePath={`produit/${product.slug}`} title={`${shortName} · Saveurs Normandes`} text={f.accroche || product.description} className="mt-8 reveal-hero" />
            </div>
          </div>
        </div>
      </section>

      {f.histoire && (
        <Section eyebrow="Chapitre I" title="L'histoire" vein="diagonal" testid="product-histoire">
          {paras(f.histoire).map((p, i) => <p key={i} className={i === 0 ? "story-drop-cap" : ""}>{p}</p>)}
        </Section>
      )}

      {f.geste && (
        <Section eyebrow="Chapitre II" title="Le geste d'Éric" vein="corner-br" testid="product-geste">
          {paras(f.geste).map((p, i, arr) => i === arr.length - 1 && arr.length > 1
            ? <blockquote key={i} className="story-quote magma-halo my-8"><FleurKintsugi size={22} variant="line" glow={false} className="shrink-0 mt-2 opacity-80" /><p className="font-vibes molten-flow leading-[1.2]" style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.2rem)" }}>« {p} »</p></blockquote>
            : <p key={i}>{p}</p>)}
        </Section>
      )}

      {(f.nez || f.bouche || f.finale) && (
        <section className="relative overflow-hidden py-14 lg:py-20 kintsugi-bg" data-testid="product-profil">
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal className="mb-10">
              <div className="smallcaps-label mb-2" style={{ fontSize: "0.65rem" }}>Chapitre III</div>
              <h2 className="font-display text-2xl md:text-3xl">{isBox ? "Profil de découverte" : "Profil aromatique"}</h2>
            </Reveal>
            <Aroma fiche={f} />
          </div>
        </section>
      )}

      {(f.rituel || f.accords?.length || f.occasion) && (
        <section className="relative overflow-hidden py-14 lg:py-20" data-testid="product-rituel">
          <KintsugiScrollVein variant="horizontal" strokes={2} opacity={0.3} />
          <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-8">
            {f.rituel && (
              <Reveal className="rounded-2xl border border-border bg-card p-7">
                <Clock className="w-5 h-5 text-primary mb-4" />
                <h3 className="font-display text-xl mb-3">Le rituel</h3>
                <p className="text-sm text-foreground/85 leading-relaxed">{f.rituel}</p>
              </Reveal>
            )}
            {f.accords?.length > 0 && (
              <Reveal delay={120} className="rounded-2xl border border-border bg-card p-7">
                <Utensils className="w-5 h-5 text-primary mb-4" />
                <h3 className="font-display text-xl mb-3">Accords mets</h3>
                <ul className="flex flex-wrap gap-2" data-testid="product-accords">
                  {f.accords.map((a) => <li key={a} className="text-xs rounded-full border border-primary/40 px-3 py-1.5 text-foreground/85">{a}</li>)}
                </ul>
              </Reveal>
            )}
            {f.occasion && (
              <Reveal delay={240} className="rounded-2xl border border-border bg-card p-7">
                <Sparkles className="w-5 h-5 text-primary mb-4" />
                <h3 className="font-display text-xl mb-3">L'occasion idéale</h3>
                <p className="text-sm text-foreground/85 leading-relaxed">{f.occasion}</p>
              </Reveal>
            )}
          </div>
        </section>
      )}

      <Reveal className="relative max-w-4xl mx-auto px-6 py-16 lg:py-24 text-center" data-testid="product-citation">
        <div className="kintsugi-divider mb-8" />
        {f.distinction && <div className="smallcaps-label mb-6" data-testid="product-distinction">{f.distinction}</div>}
        {f.citation && <p className="font-vibes molten-flow leading-[1.15] mb-8" style={{ fontSize: "clamp(2rem, 4.2vw, 3.4rem)" }}>« {f.citation} »</p>}
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link to={`/boutique?ajouter=${product.slug}`} className="btn-gold-marble inline-flex items-center gap-3 rounded-full" data-testid="product-add-to-cart-bottom">
            <ShoppingBag className="w-4 h-4" /> Commander — {product.price.toFixed(2)} €
          </Link>
          <Link to="/mon-histoire" className="inline-flex items-center gap-2 rounded-full border border-foreground/30 hover:border-primary px-6 h-11 text-sm transition-colors">
            L'histoire d'Éric <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <ShareButtons sharePath={`produit/${product.slug}`} title={`${shortName} · Saveurs Normandes`} text={f.citation || f.accroche} className="justify-center mt-8" />
        <div className="kintsugi-divider mt-8" />
      </Reveal>

      {others.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20" data-testid="product-others">
          <div className="smallcaps-label mb-6">Autres créations</div>
          <div className="grid sm:grid-cols-3 gap-6">
            {others.map((o) => (
              <Link key={o.slug} to={`/boutique/${o.slug}`} className="group rounded-2xl border border-border bg-card overflow-hidden hover-lift" data-testid={`product-other-${o.slug}`}>
                <div className="aspect-[4/3] overflow-hidden">{o.image && <img src={o.image} alt={o.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}</div>
                <div className="p-5 flex items-center justify-between gap-3">
                  <div><div className="font-display text-lg">{o.name.split("—")[0].trim()}{isNewProduct(o) && <span className="pill-new ml-2 align-middle" style={{ padding: "2px 8px", fontSize: 8 }}>Nouveau</span>}</div><div className="text-xs text-muted-foreground">{o.tagline}</div></div>
                  <div className="font-display gold-text whitespace-nowrap">{o.price.toFixed(2)} €</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
